import React, {FC, useState, useEffect} from "react"
import {map} from "lodash"

import {UserTracking as tracking, UserFlowIds} from "@irati-tracking/onboarding"
import id from "@irati/utils/id"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {H2} from "@irati/ui/src/Typography"
import {UserMetadata, DealListSort, DealStatus, Tag, TagKind} from "@irati/types"
import History from "@irati/utils/history"
import DealListComponent, {getDealList} from "@irati/deals/apollo/query/DealList"
import getCategoryFromTag from "@irati/deals/helpers/categories/getCategoryFromTag"
import FeedSliderGraph from "../FeedSlider"
import NoDealsInCategory from "./NoDealsInCategory"
import UnavailableCategoryModal from "./UnavailableCategoryModal"
import {CategorySelector, EditSelector, StyledHeader, CategoriesWrapper} from "./styled"

interface Props extends InjectIntl {
  metadata: UserMetadata,
  idNamespace: string,
  removeUnavailableCategories: () => Promise<any>
}

const SLIDER_VARIABLES = {statuses: [DealStatus.On, DealStatus.Unavailable], limit: 9, sort: DealListSort.Date}

const CustomCategories: FC<Props> = ({metadata, removeUnavailableCategories, formatMessage, idNamespace}) => {
  const [selectedCategory, setSelectedCategory] = useState<Tag | null>(null)

  if (!metadata) return null
  const {
    config: {favouriteCategories}
  } = metadata

  if (!favouriteCategories || favouriteCategories.length === 0) {
    return null
  }

  useEffect(() => {
    if (!selectedCategory) {
      if (favouriteCategories.length > 0) {
        selectCategoryOrTag(favouriteCategories[0])
        setUnavailableCategories(favouriteCategories.filter(({filterable}) => !filterable))
      }
    }
  }, [])
  const [unavailableCategories, setUnavailableCategories] = useState<Tag[]>([])
  const [showUnavailableCategoryModal, setShowUnavailableCategoryModal] = useState(true)

  const selectCategoryOrTag = (selectedCategoryOrTag: Tag) => {
    if (selectedCategoryOrTag?.kind === TagKind.DealCategory) {
      setSelectedCategory(selectedCategoryOrTag)
    }
    getCategoryFromTag(selectedCategoryOrTag.uuid).then((parentCategory) => {
      const url = parentCategory ? `${parentCategory.url}?tags=${selectedCategoryOrTag.url}` : selectedCategoryOrTag.url
      setSelectedCategory({...selectedCategoryOrTag, url})
    })
  }

  const onClick = (selectedId: string) => {
    const selectedCategoryOrTag = favouriteCategories.find(({uuid}) => uuid === selectedId)
    if (selectedCategoryOrTag) {
      tracking.perksHomepageCategoryClicked({
        category_id: selectedId,
        category_name: selectedCategoryOrTag.name,
        flow: UserFlowIds.CategoriesCustomisation
      })
      selectCategoryOrTag(selectedCategoryOrTag)
    }
  }

  const editClick = () => {
    tracking.perksHomepageEditClicked({
      flow: UserFlowIds.CategoriesCustomisation
    })
    History.push("/account/settings/customisation/categories")
  }

  const seeMoreClick = () => {
    if (selectedCategory) {
      tracking.perksHomepageSeeMoreClicked({
        category_id: selectedCategory.uuid,
        category_name: selectedCategory.name,
        flow: UserFlowIds.CategoriesCustomisation
      })
    }
  }

  const updateLinkClick = () => {
    if (selectedCategory) {
      tracking.perksHomepageNoDealsLinkClicked({
        category_id: selectedCategory.uuid,
        category_name: selectedCategory.name,
        flow: UserFlowIds.CategoriesCustomisation
      })
    }
    History.push("/account/settings/customisation/categories")
  }

  const trackNoDealsForSelectedCategory = () => {
    if (selectedCategory) {
      tracking.perksHomepageSelectedCategoryWithNoDeals({
        category_id: selectedCategory.uuid,
        category_name: selectedCategory.name,
        flow: UserFlowIds.CategoriesCustomisation
      })
    }
  }

  const hideModal = () => {
    tracking.perksHomepageCategoryRemovedModalDismissed({
      category_ids: map(unavailableCategories, "uuid"),
      category_names: map(unavailableCategories, "name"),
      flow: UserFlowIds.CategoriesCustomisation
    })
    removeUnavailableCategories(unavailableCategories)
    setShowUnavailableCategoryModal(false)
  }

  const navigateToCategories = () => {
    tracking.perksHomepageCategoryRemovedModalNavigate({
      category_ids: map(unavailableCategories, "uuid"),
      category_names: map(unavailableCategories, "name"),
      flow: UserFlowIds.CategoriesCustomisation
    })
    removeUnavailableCategories(unavailableCategories).then(() => {
      History.push("/account/settings/customisation/categories")
    })
  }

  const variables = selectedCategory
    ? {
      ...SLIDER_VARIABLES,
      tags: [selectedCategory.uuid].concat(selectedCategory.childrenIds || [])
    }
    : undefined

  return (
    <>
      <H2 marginTop={5}>
        <FormattedMessage id="perks.containers.homepage.customisation.header" />
      </H2>
      <CategoriesWrapper marginTop={2}>
        {favouriteCategories.map(({name, uuid}) => (
          <CategorySelector key={uuid} name={name} uuid={uuid} onClick={onClick} selected={uuid === selectedCategory?.uuid} />
        ))}
        <EditSelector name={formatMessage({id: "perks.containers.homepage.customisation.addEdit"})} onClick={editClick} />
      </CategoriesWrapper>
      {selectedCategory && variables && (
        <DealListComponent variables={variables}>
          {(apollo) => {
            const {results} = getDealList(apollo)
            const categoryHasDeals = results.length > 0
            if (!categoryHasDeals) {
              trackNoDealsForSelectedCategory()
            }

            return (
              <>
                <StyledHeader
                  id={id("custom-categories-header", idNamespace)}
                  linkLabel={categoryHasDeals ? "perks.containers.homepage.customisation.moreLink" : null}
                  linkTo={categoryHasDeals ? `/categories/${selectedCategory.url}` : null}
                  onClick={seeMoreClick}>
                  {selectedCategory?.name}
                </StyledHeader>
                {categoryHasDeals && (
                  <FeedSliderGraph
                    id={id("custom-categories-slider", idNamespace)}
                    sectionHeaderText="customised interest"
                    data={results}
                    showSectionHeader={false} />
                )}
                {!categoryHasDeals && <NoDealsInCategory updateLinkClick={updateLinkClick} />}
                {unavailableCategories.length > 0 && showUnavailableCategoryModal && (
                  <UnavailableCategoryModal
                    unavailableCategories={unavailableCategories}
                    hideModal={hideModal}
                    navigateToCategories={navigateToCategories}
                    showCustomisationLink />
                )}
              </>
            )
          }}
        </DealListComponent>
      )}
    </>
  )
}

export default injectIntl(CustomCategories)
